import {Collapse} from '@mui/material';
import {
  ConfigObjectType,
  DateUtils,
  LightIconButton,
  TranslateUtils,
  omitNullsy,
  rawRenderingLib,
  serializeJSONToString,
  useApiQuery,
} from '@wallaby-ui/core';
import {
  AG_GRID_COMPATIBILITY,
  clampPageNumber,
  DateTimeFormatter,
  DEFAULT_PAGE_SIZE,
  DocumentNameFormatterV2,
  DocumentsListDepartmentFormatter,
  FILTER_ACTIVE_ICON_COLORS,
  getDefaultMTOptions,
  JustTooltipFormatter,
  LightDateRangeFilter,
  LightOrganizationFilter2,
  LightSavedFiltersButton,
  LightSelectedFilters,
  LightSelectFilter,
  LightTextFilter,
  MaterialTableWrapper,
  ShortcutEnum,
  TablePaging,
  TableSorting,
  useFormatters,
  useTableData,
  WallabyCoreSchema,
} from '@wallaby-ui/document';
import {parseISO} from 'date-fns';
import {isEqual} from 'lodash';
import {Column} from 'material-table';
import React, {useCallback, useEffect, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {useStore} from 'react-redux';

import {HospitalizationState} from './HospitalizationDetailLayoutV2';

type EncounterType = WallabyCoreSchema.components['schemas']['EncounterDetailDto'];
function getHospitalizationLabel(value: EncounterType, intl: any, store: any) {
  const startedAt = DateUtils.getFormattedDate(parseISO(value.startedAt), intl, true, true);
  const endedAt = value.endedAt
    ? DateUtils.getFormattedDate(parseISO(value.endedAt), intl, true, true)
    : TranslateUtils.translate('patient.card.ongoing', intl);
  const translatedType = rawRenderingLib.translatedEnumValueRenderer(
    {
      enumName: 'EncounterTypeEnum',
      enumValueName: value.type,
    },
    {intl, store}
  );
  return value.type === 'OUTPATIENT'
    ? `${value.organization.name}, ${startedAt} (${translatedType})`
    : `${value.organization.name}, ${startedAt} - ${endedAt} (${translatedType})`;
}

type RowType = WallabyCoreSchema.components['schemas']['DocumentForEncounterDto'];
function getColumns(_state: HospitalizationState, intl: any): Column<RowType>[] {
  const {formatDate, formatDateTime} = useFormatters();
  return [
    {
      field: 'documentName',
      title: TranslateUtils.translate('documents.list.documentName', intl),
      render: (row: RowType) =>
        row && <DocumentNameFormatterV2 {...AG_GRID_COMPATIBILITY} value={TranslateUtils.translate(row.name, intl)} data={row} />,
      sorting: false,
    },
    {
      field: 'organization',
      title: TranslateUtils.translate('documents.list.origin', intl),
      render: (row: RowType) =>
        row && <DocumentsListDepartmentFormatter {...AG_GRID_COMPATIBILITY} value={row.organizationShortName} data={row} />,
      width: '90px',
      sorting: false,
    },
    {
      field: 'date',
      title: TranslateUtils.translate('documents.list.date', intl),
      tooltip: TranslateUtils.translate('documents.list.date.tooltip', intl),
      render: (row: RowType) => {
        return (
          <JustTooltipFormatter
            {...AG_GRID_COMPATIBILITY}
            value={formatDate(row.effectiveAt)}
            tooltipOverride={formatDateTime(row.effectiveAt)}
          />
        );
      },
      width: '127px',
      defaultSort: 'desc',
      customSort(a, b) {
        const key = (v) => parseISO(v.effectiveAt);
        const diff = +(key(a) > key(b)) - +(key(a) < key(b));
        return diff;
      },
    },
  ];
}

type PageNumberParams = {
  scope: 'PATIENT_NARROWED_BY_HOSPITALIZATION';
  encounterId: number;
  documentVersionId: string;
  size: number;
};
type LoadDocumentsParams = {
  documentName: string | null;
  documentOrganizationIds: string[] | null;
  documentLastUpdatedFrom: string | undefined | null;
  documentLastUpdatedTo: string | undefined | null;
  hospitalizationsOfOrigin: number[] | null;
  defaultDocumentDateRangeStart: number;
  defaultDocumentDateRangeEnd?: number;
  surgeryProtocolDateRangeStart: number;
} & {
  page: number;
  size: number;
  sort: string | null;
};

export type DocumentsFilters = {
  documentName?: string;
  documentOrganizationIds?: string[];
  documentLastUpdatedFrom?: string;
  documentLastUpdatedTo?: string;
  documentLastUpdatedFloatingShortcutName?: ShortcutEnum | null;
  hospitalizationsOfOrigin?: number[];
};
export const DEFAULT_DOCUMENTS_FILTERS = (encounterId: number | undefined): DocumentsFilters => ({
  hospitalizationsOfOrigin: encounterId != null ? [encounterId] : undefined,
});
export const DEFAULT_DOCUMENTS_SORTING: TableSorting = {
  field: 'date',
  direction: 'desc',
};
export const DEFAULT_DOCUMENTS_PAGING: TablePaging = {};

type Props = {
  patientCardId: number;
  hospitalizationId: string;
  encounterId: number | undefined;
  documentId: string;
  config: ConfigObjectType;
  state: HospitalizationState;
  changeState: (diff: Partial<HospitalizationState>) => void;
};
export const HospitalizationDocumentsV2: React.FC<Props> = (props) => {
  const {patientCardId, hospitalizationId, encounterId, documentId, config, state, changeState} = props;
  const intl = useIntl();
  const store = useStore();
  const hasSorting = useMemo(
    () => !isEqual(omitNullsy(state.documentsSorting), omitNullsy(DEFAULT_DOCUMENTS_SORTING)),
    [state.documentsSorting]
  );
  const haveDefaultFilters = encounterId != null;
  const defaultDocumentsFilters = useMemo(() => DEFAULT_DOCUMENTS_FILTERS(encounterId), [encounterId]);
  const hasFilters = useMemo(
    () => !isEqual(omitNullsy(state.documents), omitNullsy(defaultDocumentsFilters)),
    [state.documents, defaultDocumentsFilters]
  );
  // select first document by default
  const DEFAULT_DOCUMENTS_PARAMS: Omit<LoadDocumentsParams, 'sort'> = useMemo(
    () => ({
      page: state.documentsPaging.page ?? 0,
      size: state.documentsPaging.pageSize ?? DEFAULT_PAGE_SIZE,
      documentName: state.documents.documentName ?? '',
      documentOrganizationIds: state.documents.documentOrganizationIds ?? '',
      documentLastUpdatedFrom: state.documents.documentLastUpdatedFrom ?? '',
      documentLastUpdatedTo: state.documents.documentLastUpdatedTo ?? '',
      hospitalizationsOfOrigin: state.documents.hospitalizationsOfOrigin ?? '',
      defaultDocumentDateRangeStart: 365,
      defaultDocumentDateRangeEnd: 365,
      surgeryProtocolDateRangeStart: 365,
    }),
    [state]
  );
  const storageIdPrefix = `/hospitalizedPatients/v2/${hospitalizationId}`;
  /*
  // automatically navigate to first document
  const firstPageData = useTableData<LoadDocumentsParams, RowType[]>(
    `${config.service.url.documents}${encounterId}`,
    {...DEFAULT_DOCUMENTS_PARAMS, sort: 'srcCreatedOn,desc'},
    'content',
    haveDefaultFilters
  );
  const dispatch = useAppDispatch();
  const [query] = useLocationQuery(storageIdPrefix);
  const navigateTo = useCallback(
    (route: string, params) => {
      dispatch(locationContainer.navigateToRouteWithParams(locationContainer.LOCATION_REDUCER_STATE_ID, route, params));
    },
    [dispatch]
  );
  useEffect(() => {
    if (firstPageData.data?.[0]?.documentVersionId && !documentId) {
      const firstDocumentId = firstPageData.data[0].documentVersionId;
      navigateTo(`/hospitalizedPatients/v2/${hospitalizationId}/document/${firstDocumentId}`, {
        ...query,
        leftTabs: {...(query.leftTabs ?? {}), reuseDocsPage: false},
      } as HospitalizationState);
    }
  }, [firstPageData, navigateTo, hospitalizationId, documentId, query]);
  */

  // set page number from documentId
  const needDefaultPageNumber = !state.leftTabs.reuseDocsPage && state.documentsPaging.page == null;
  const pageNumberUrl = `${config.service.url.documents}${encounterId}/document-version/${documentId}/page-index`;
  const pageNumberApiParams: PageNumberParams = useMemo(
    () => ({
      scope: 'PATIENT_NARROWED_BY_HOSPITALIZATION',
      encounterId: encounterId ?? -1,
      documentVersionId: documentId,
      size: state.documentsPaging.pageSize ?? DEFAULT_PAGE_SIZE,
    }),
    [encounterId, documentId, state.documentsPaging.pageSize]
  );
  const pageNumberApiParamsWithSorting = useMemo(() => {
    const params = new URLSearchParams(pageNumberApiParams as Record<string, any>);
    params.append('sort', `EFFECTIVE_AT,${state.documentsSorting.direction ?? 'desc'}`);
    params.append('sort', `VERSION,${state.documentsSorting.direction ?? 'desc'}`);
    return params;
  }, [pageNumberApiParams, state.documentsSorting]);
  const {data: defaultPageNumber} = useApiQuery<number>(
    [pageNumberUrl, serializeJSONToString(pageNumberApiParams), state.documentsSorting],
    null,
    {
      axiosConfig: {
        url: pageNumberUrl,
        params: pageNumberApiParamsWithSorting,
      },
      reactQueryOptions: {
        enabled: needDefaultPageNumber && Boolean(documentId),
        staleTime: 0,
        refetchOnMount: 'always',
      },
    }
  );
  useEffect(() => {
    if (!needDefaultPageNumber || defaultPageNumber === undefined) return;
    setTimeout(() => {
      // NOTE: run after table callback
      changeState({
        documentsPaging: {
          ...state.documentsPaging,
          page: defaultPageNumber,
        },
        leftTabs: {
          ...state.leftTabs,
          reuseDocsPage: true,
        },
      });
    }, 0);
    // eslint-disable-next-line
  }, [needDefaultPageNumber, defaultPageNumber]);

  // hospitalizations
  const hospitalizationsUrl = `${config.service.url.patientsRoot}/${patientCardId}/encounters`;
  const {data: hospitalizationsData, isLoading: hospitalizationsDataIsLoading} = useApiQuery<EncounterType[]>(
    [hospitalizationsUrl, patientCardId],
    null,
    {axiosConfig: {url: hospitalizationsUrl}}
  );
  // data
  const apiParams = useMemo(() => {
    const params = new URLSearchParams(DEFAULT_DOCUMENTS_PARAMS as Record<string, any>);
    params.append('sort', `EFFECTIVE_AT,${state.documentsSorting.direction ?? 'desc'}`);
    params.append('sort', `VERSION,${state.documentsSorting.direction ?? 'desc'}`);
    return params;
  }, [DEFAULT_DOCUMENTS_PARAMS, state.documentsSorting]);
  const documentsData = useTableData<LoadDocumentsParams, RowType[]>(
    `${config.service.url.documents}${encounterId}`,
    apiParams,
    'content',
    haveDefaultFilters
  );
  const isLoading = documentsData.isLoading || hospitalizationsDataIsLoading;
  const columns = getColumns(state, intl);
  return (
    <>
      <div className='dl-header-buttons-row-wide' style={{justifyContent: 'flex-end'}}>
        <div className='dl-header-icons-row' style={{gap: 6}}>
          <LightSelectedFilters
            width='auto'
            marginBottom='0px'
            hasSorting={hasSorting}
            handleResetSorting={() => changeState({documentsSorting: DEFAULT_DOCUMENTS_SORTING})}
            hasFilters={hasFilters}
            handleResetFilters={() => changeState({documents: defaultDocumentsFilters})}
          />
          <LightIconButton
            iconName='filterIcon'
            useFixedViewBox='0 0 20 16'
            style={{width: 20, height: 16}}
            title={state.areFiltersOpen.documents ? 'layout.filters.closeFilters' : 'layout.filters.openFilters'}
            onClick={() => changeState({areFiltersOpen: {...state.areFiltersOpen, documents: !state.areFiltersOpen.documents}})}
            selected={state.areFiltersOpen.documents}
            colors={hasFilters && !state.areFiltersOpen.documents ? FILTER_ACTIVE_ICON_COLORS : undefined}
            dataName={'light-icon-button-documents'}
          />
          <LightSavedFiltersButton
            storageIdPrefix={`${storageIdPrefix}.documents`}
            component='hospitalizationDocuments'
            config={config}
            filter={state.documents}
            setFilter={(newFilter) => changeState({documents: newFilter as DocumentsFilters, documentsPaging: {...state.documentsPaging, page: 0}})}
          />
        </div>
      </div>
      <Collapse
        in={state.areFiltersOpen.documents}
        timeout={500}
        style={{
          minHeight: state.areFiltersOpen.documents ? 'unset' : 0,
          marginTop: 4,
          marginBottom: 8,
        }}
      >
        <div className='dl-filters-row'>
          <LightTextFilter
            config={{
              name: 'documentName',
              ui: {label: 'document.filters.documentname'},
            }}
            value={state.documents.documentName}
            onChange={(newValue) =>
              changeState({
                documents: {
                  ...state.documents,
                  documentName: newValue ?? undefined,
                },
                documentsPaging: {
                  ...state.documentsPaging,
                  page: 0,
                },
              })
            }
          />
          <LightDateRangeFilter
            config={{
              name: 'documentLastUpdated',
              ui: {label: 'document.filters.documentdate'},
            }}
            value={
              state.documents.documentLastUpdatedFrom && state.documents.documentLastUpdatedTo
                ? {
                    startDate: state.documents.documentLastUpdatedFrom,
                    endDate: state.documents.documentLastUpdatedTo,
                    shortcut: state.documents.documentLastUpdatedFloatingShortcutName,
                  }
                : null
            }
            onChange={(newValue) => {
              changeState({
                documents: {
                  ...state.documents,
                  documentLastUpdatedFrom: newValue?.startDate,
                  documentLastUpdatedTo: newValue?.endDate,
                  documentLastUpdatedFloatingShortcutName: newValue?.shortcut,
                },
                documentsPaging: {
                  ...state.documentsPaging,
                  page: 0,
                },
              });
            }}
            dateOnly
            showShortcuts
          />
          <LightOrganizationFilter2
            appConfig={config}
            config={{
              name: 'documentOrganizationIds',
              ui: {label: 'document.filters.doc_org'},
            }}
            multi
            value={state.documents.documentOrganizationIds}
            onChange={(newOrganizationIds) => {
              changeState({
                documents: {
                  ...state.documents,
                  documentOrganizationIds: newOrganizationIds,
                },
                documentsPaging: {
                  ...state.documentsPaging,
                  page: 0,
                },
              });
            }}
          />
          <LightSelectFilter
            config={{
              name: 'hospitalizationsOfOrigin',
              ui: {label: 'document.filters.hospitalization_of_origin'},
            }}
            options={(hospitalizationsData ?? []).map((v) => ({
              id: v.id,
              label: getHospitalizationLabel(v, intl, store),
            }))}
            value={state.documents.hospitalizationsOfOrigin}
            onChange={(newHospitalizationIds) => {
              changeState({
                documents: {
                  ...state.documents,
                  hospitalizationsOfOrigin: newHospitalizationIds,
                },
                documentsPaging: {
                  ...state.documentsPaging,
                  page: 0,
                },
              });
            }}
            multi
          />
        </div>
      </Collapse>
      <div style={{flex: 1, overflowY: 'auto'}}>
        <MaterialTableWrapper
          variant='big'
          style={{maxHeight: '100%'}}
          getRowClass={(rowData, index) => {
            if (documentId && rowData.documentVersionId === +documentId) {
              return 'dlAgGridPreSelectedRow';
            }
            return index % 2 === 0 ? 'ag-row-even' : 'ag-row-odd';
          }}
          options={getDefaultMTOptions(documentsData.countOnPage ?? DEFAULT_PAGE_SIZE)}
          useMaxHeight
          isLoading={isLoading}
          columns={columns}
          data={documentsData?.data ?? []}
          totalCount={documentsData?.totalCount}
          page={documentsData?.page}
          totalPages={documentsData?.totalPages}
          paginationOptions={{
            showPageSize: false,
            showResultCount: false,
            showMoveToPage: false,
            maxPageClickers: 2,
          }}
          onChangePage={(page, pageSize) =>
            changeState({
              documentsPaging: {
                ...state.documentsPaging,
                page,
                pageSize,
              },
            })
          }
          onChangeRowsPerPage={(pageSize) =>
            changeState({
              documentsPaging: {
                ...state.documentsPaging,
                pageSize,
                page: clampPageNumber(state.documentsPaging.page, pageSize, documentsData.totalCount),
              },
            })
          }
          onOrderChange={(colIndex, direction) => {
            const field = columns[colIndex].field as string;
            changeState({
              documentsSorting: {
                field,
                direction,
              },
            });
          }}
          controlledSort={(() => {
            return {
              field: 'date',
              direction: state.documentsSorting.direction,
            };
          })()}
          controlledPageSize={state.documentsPaging.pageSize ?? DEFAULT_PAGE_SIZE}
          scrollStorageId={`${storageIdPrefix}.documentsScrollXY`}
          direction={state.documentsSorting.direction}
        />
      </div>
    </>
  );
};
